import { useEffect, useState } from 'react'
import { Search, MapPin, Monitor, X, LogOut, User, CalendarCheck } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useSessionStore, clearPersistedCart } from '@/stores/sessionStore'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { useStore } from '@/api/stores'
import { v2, v2api } from '@/api/v2'
import { beginLogout, endLogout } from '@/api/client'
import { CommandPalette } from './CommandPalette'
import { UpdatePill } from '@/components/UpdateNotice'
import { useAgeRestriction } from '@/hooks/useAgeRestriction'

// "Jeremy S." badge label. Tries the structured fields first; if
// those are blank (the /auth/clerk response only carries `username`,
// which we stash as `full_name`), splits the full name on whitespace.
// Capitalizes because the BE stores names lowercase.
function capitalize(s: string | null | undefined): string {
  if (!s) return ''
  const t = s.trim()
  if (!t) return ''
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()
}
function formatStaffName(staff: {
  first_name?: string | null
  last_name?: string | null
  full_name?: string | null
}): string {
  // Pull from the most-structured source available, then fall back
  // to splitting `full_name` (the only field clerk PIN login fills).
  let first = (staff.first_name ?? '').trim()
  let last = (staff.last_name ?? '').trim()
  if (!first && !last && staff.full_name) {
    const parts = staff.full_name.trim().split(/\s+/).filter(Boolean)
    first = parts[0] ?? ''
    last = parts.length > 1 ? parts[parts.length - 1] : ''
  }
  const f = capitalize(first)
  const lastInitial = last ? last.charAt(0).toUpperCase() : ''
  if (f && lastInitial) return `${f} ${lastInitial}.`
  return f || capitalize(last) || ''
}

export function Header() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [commandOpen, setCommandOpen] = useState(false)
  const { selectedStore, selectedRegister, staff, setStore } = useSessionStore()
  const { clearCart, isRefundMode, exitRefundMode, refundSourceReceipt } = usePOSStore()
  const { showConfirm } = useUIStore()
  // Standing "must be born on or before" reminder. V1 rendered the same
  // line in the POS chrome (waio-portal templates/core/pos/index_3.html:
  // `Legal Age: {{ store.restriction_dob_str }}`). Shown whenever the
  // store has an age restriction — independent of
  // `pos.confirmation_of_age_limit`, which gates only the checkout prompt.
  const ageRestriction = useAgeRestriction()

  // Hydrate the real store name from /v2/objects/stores/{id}. Auth
  // responses don't carry the store name, so login leaves selectedStore
  // with a synthesized "Store {id}" placeholder. Once the store record
  // resolves, write the real name back into the session so every
  // consumer (Header, settings, receipts, etc.) sees the correct label.
  const { data: storeDetails } = useStore(selectedStore?.id ?? null)
  useEffect(() => {
    if (!selectedStore || !storeDetails) return
    const realName = typeof storeDetails.name === 'string' ? storeDetails.name.trim() : ''
    if (!realName || realName === selectedStore.name) return
    setStore({ ...selectedStore, name: realName })
  }, [selectedStore, storeDetails, setStore])

  const handleCancelRefund = () => {
    exitRefundMode()
    // Invalidate refund search so incomplete refunds don't show
    queryClient.invalidateQueries({ queryKey: ['receipts', 'refund-search'] })
  }

  const handleLogout = () => {
    showConfirm({
      title: 'Sign Out?',
      message: 'Sign out and lock the register? The next staff member will need to enter their PIN. Your cart will be cleared.',
      confirmLabel: 'Sign Out',
      variant: 'danger',
      onConfirm: async () => {
        // Clerk-only logout — drop the PIN session but keep the
        // register-scoped session token so the next cashier lands
        // straight on the PIN screen instead of re-running the
        // license/auth + register-select flow. The /login route's
        // guard requires sessionToken + selectedRegister; both stay
        // intact, only `isAuthenticated` flips to false.
        //
        // Critical: flip `beginLogout()` first. Any in-flight
        // register-scoped query that resolves after we POST
        // /clerk/logout will see a 401 (backend dropped the clerk
        // session). Without the flag, the response interceptor's
        // retry path treats that 401 as "session is dead" and
        // hard-reloads to /auth, clobbering our /login navigate.
        // The flag tells the interceptor to silently reject during
        // logout instead.
        beginLogout()
        try {
          // SECURITY: clear the local cart (in-memory AND persisted to
          // localStorage) BEFORE flipping isAuthenticated. Previously
          // clearCart()/clearPersistedCart() ran after the awaited
          // /clerk/logout POST, opening a 200ms-1s window between
          // navigation-to-login and the actual localStorage wipe.
          // During that window Staff B could enter their PIN, log in,
          // and watch posStore/cartStore rehydrate from localStorage
          // -- exposing Staff A's cart to Staff B. The local clears
          // have no network or backend dependency; they can run
          // synchronously up front. Zustand setState batches React
          // updates within this event handler, so the brief flash
          // of empty cart never paints -- the navigate below replaces
          // the view in the same frame.
          clearCart()
          clearPersistedCart()

          // Flip `isAuthenticated` and navigate BEFORE the
          // /clerk/logout POST so POSMainPage unmounts and its
          // register-scoped query observers detach. If we POSTed
          // first, the backend would kill the clerk session while
          // useInfiniteProducts/useTopSellers/useEcomOrders/
          // useHeldOrders were still active — every refetch races
          // back as a 403, piles up in the refresh-subscriber
          // queue, and the failed refresh leaves React Query
          // thinking the products query is still in-flight on the
          // next PIN login. Clerk token stays in place so the
          // POST below still authenticates.
          useSessionStore.setState({ isAuthenticated: false })
          navigate({ to: '/login' })

          try {
            await v2api.post(v2('/clerk/logout'), {})
          } catch (err) {
            console.warn('[handleLogout] backend logout failed; clearing local state anyway', err)
          }
          queryClient.clear()
          useSessionStore.setState({
            clerkToken: null,
            staff: null,
            isClockedIn: false,
            clockedInAt: null,
          })
        } finally {
          // Lift the flag after a beat — long enough for any racing
          // queries kicked off before the navigate to settle. 1s is
          // generous; the network round-trip for the failing requests
          // typically completes well under that.
          setTimeout(endLogout, 1000)
        }
      },
    })
  }

  const handleChangeRegister = () => {
    navigate({ to: '/select-register' })
  }

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-3 md:px-6 h-14 flex items-center flex-shrink-0">
        {/* Logo */}
        <div className="flex flex-col items-center -space-y-1">
          <svg
            className="h-5 w-auto text-[#4b7]"
            viewBox="0 0 1823.05 552.04"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="WAIO"
          >
            <path d="M1252.22,136.75a42.66,42.66,0,0,0-42.66,42.66V500.67a42.66,42.66,0,0,0,85.32,0V179.41A42.66,42.66,0,0,0,1252.22,136.75Z" />
            <path d="M1608.88,128.05c-115.79,0-213.3,81.84-213.3,210.69S1493.09,552,1608.88,552s214.17-84.45,214.17-213.3S1724.67,128.05,1608.88,128.05Zm0,343c-69.65,0-128-53.11-128-132.33,0-78.36,58.33-130.6,128-130.6s128.85,52.24,128.85,130.6C1737.73,418,1678.54,471.07,1608.88,471.07Z" />
            <path d="M1060.53,136.75a42.66,42.66,0,0,0-41.22,31.82C982.88,142.44,937.5,128.05,889,128.05c-115.79,0-213.3,81.84-213.3,210.69S773.23,552,889,552c48.38,0,93.68-14.79,130.08-41.36a42.64,42.64,0,0,0,84.09-10V179.41A42.66,42.66,0,0,0,1060.53,136.75ZM889,471.07c-69.65,0-128-53.11-128-132.33,0-78.36,58.33-130.6,128-130.6s128.85,52.24,128.85,130.6C1017.87,418,958.67,471.07,889,471.07Z" />
            <path d="M577.58,129.36a43.15,43.15,0,0,0-40.71,28.85L470,348.59c-7.07,20.14-35.56,20.1-42.58-.06L365.19,169.59C358,149,340.43,133,318.83,129.93a68.84,68.84,0,0,0-16.89,0c-21.61,3-39.18,19-46.36,39.66L193.32,348.53c-7,20.16-35.51,20.2-42.58.06L83.9,158.21A43.15,43.15,0,1,0,2.84,187.8L125.55,511.57a43.16,43.16,0,0,0,40.35,27.86h10.7a43.15,43.15,0,0,0,40.74-28.92L289,305.2a22.63,22.63,0,0,1,42.68,0l71.71,205.31a43.15,43.15,0,0,0,40.73,28.92h10.7a43.16,43.16,0,0,0,40.36-27.86L617.93,187.8A43.15,43.15,0,0,0,577.58,129.36Z" />
            <path d="M1252.22,0a42.66,42.66,0,1,0,42.66,42.66A42.66,42.66,0,0,0,1252.22,0Z" />
          </svg>
        </div>

        {/* Search Trigger */}
        <div className="flex-1 md:flex-initial mx-2 md:mx-8 max-w-md">
          {isRefundMode && refundSourceReceipt ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <span className="text-sm font-medium text-red-800">
                Refunding Receipt #{refundSourceReceipt.number}
              </span>
              <button
                onClick={handleCancelRefund}
                className="ml-auto p-1 hover:bg-red-200 rounded transition-colors"
                title="Exit Refund Mode"
              >
                <X size={16} className="text-red-600" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setCommandOpen(true)}
              className="flex items-center gap-3 w-full md:w-72 px-3 py-2 bg-gray-100 hover:bg-gray-200/70 rounded-lg text-sm text-gray-500 transition-all cursor-pointer"
            >
              <Search size={18} className="text-gray-500" />
              <span className="flex-1 text-left">Search...</span>
              <kbd className="hidden md:inline px-1.5 py-0 bg-white border border-gray-300 rounded-md text-[10px] font-semibold text-gray-500">
                ⌘K
              </kbd>
            </button>
          )}
        </div>

        {/* Info Badges */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Age-restriction reminder. Amber (not gray) so it reads as a
              compliance notice rather than another status pill, and kept
              leftmost in the badge row so it is never the first thing
              dropped as the row tightens. */}
          {ageRestriction.age !== null && (
            <div
              className="flex items-center gap-1 px-3 h-9 bg-amber-100 text-amber-900 rounded-lg text-xs font-semibold"
              title={`Legal age ${ageRestriction.age}: customer must be born on or before ${ageRestriction.cutoffLabel}`}
            >
              <CalendarCheck size={14} />
              <span className="hidden lg:inline">Born on or before</span>
              <span>{ageRestriction.cutoffLabel}</span>
            </div>
          )}

          <UpdatePill />

          {/* Signed-in clerk — left of the location for the same
              "who, where, which" reading order the receipt header uses.
              Renders "First L." (first name + last initial) so a long
              full name doesn't push the location pill off-screen.
              Force-capitalize because the BE stores names lowercase
              and we don't want "jeremy s." in the chrome. */}
          {staff && (
            <div className="hidden md:flex items-center gap-1.5 px-3 h-9 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold">
              <User size={14} />
              <span>{formatStaffName(staff)}</span>
            </div>
          )}

          {/* Store & Register */}
          <button
            onClick={handleChangeRegister}
            className="flex items-center gap-2 px-3 h-9 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
          >
            <MapPin size={14} />
            <span className="hidden md:inline">{selectedStore?.name || 'No Store'}</span>
          </button>

          <div className="hidden md:flex items-center gap-1.5 px-3 h-9 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold">
            <Monitor size={14} />
            <span>{selectedRegister?.name || 'No Register'}</span>
          </div>

          {/* Sign out clerk — returns to PIN screen, keeps register */}
          <button
            onClick={handleLogout}
            title="Sign out (return to PIN)"
            className="h-9 w-9 bg-green-100 hover:bg-green-200 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
          >
            <LogOut size={16} className="text-green-700" />
          </button>
        </div>
      </header>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </>
  )
}
